import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CourseAdminApi } from '../../services/api/CourseAdminApi';
import CreateCourseModal from '../../components/courses/CreateCourseModal';
import './CourseAdminHome.css';

/**
 * Course-Admin Landing Page — "My Classes".
 *
 * Route: /my-classes
 * Shows every course the current user can administer, with links to the
 * per-course admin dashboard and an inline "Create Class" form for org-owned
 * courses (when the user belongs to at least one org).
 */
function CourseAdminHome() {
  const { orgMemberships } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  const adminOrgs = (orgMemberships || []).filter(
    m => m.status === 'active' && (m.orgRole === 'org_admin' || m.orgRole === 'course_admin')
  );

  const loadCourses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await CourseAdminApi.getMyAdminCourses();
      if (!resp.ok) { setError(`Failed to load courses (${resp.status})`); return; }
      setCourses(await resp.json());
    } catch (e) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  const openWizard = () => {
    // If user only belongs to one admin org, preselect it; otherwise default to '' (personal/none).
    if (adminOrgs.length === 1) setSelectedOrgId(String(adminOrgs[0].organizationId));
    else setSelectedOrgId('');
    setShowWizard(true);
  };

  return (
    <div className="course-admin-home">
      <header className="course-admin-home-header">
        <h1>My Classes</h1>
        <button
          className="course-admin-home-create-btn"
          onClick={openWizard}
        >
          + Create Class
        </button>
      </header>

      {error && <div className="course-admin-home-error" role="alert">{error}</div>}

      {showWizard && adminOrgs.length > 1 && !selectedOrgId && (
        <div className="course-admin-home-form" style={{ marginBottom: 12 }}>
          <label>
            Create under organization
            <select
              value={selectedOrgId}
              onChange={e => setSelectedOrgId(e.target.value)}
            >
              <option value="">Personal course</option>
              {adminOrgs.map(m => (
                <option key={m.organizationId} value={m.organizationId}>
                  {m.organizationName || `Org #${m.organizationId}`}
                </option>
              ))}
            </select>
          </label>
          <div className="course-admin-home-form-actions">
            <button type="button" onClick={() => setSelectedOrgId('__personal__')}>Continue as Personal</button>
            <button type="button" onClick={() => setShowWizard(false)}>Cancel</button>
          </div>
        </div>
      )}

      <CreateCourseModal
        open={showWizard && (adminOrgs.length <= 1 || selectedOrgId !== '')}
        orgId={selectedOrgId && selectedOrgId !== '__personal__' ? selectedOrgId : null}
        onClose={() => { setShowWizard(false); setSelectedOrgId(''); }}
        onCreated={() => { setShowWizard(false); setSelectedOrgId(''); loadCourses(); }}
      />

      {loading && <div className="course-admin-home-loading">Loading…</div>}

      {!loading && courses.length === 0 && (
        <div className="course-admin-home-empty">
          No courses to administer yet.
        </div>
      )}

      {!loading && courses.length > 0 && (
        <table className="course-admin-home-table">
          <thead>
            <tr>
              <th>Course</th>
              <th>Type</th>
              <th>Ownership</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {courses.map(c => (
              <tr key={c.endeavorId}>
                <td>{c.name || `Course ${c.endeavorId}`}</td>
                <td>{c.isShared ? 'Shared' : 'Personal'}</td>
                <td>{c.ownershipType || '—'}</td>
                <td>
                  <Link to={`/course/${c.endeavorId}/admin`}>Manage →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default CourseAdminHome;
